<template>
  <div class="pr-2">
    <template
      v-if="db_line_items && db_line_items.length && db_line_items.length > 0"
    >
      <!-- Top Settings Bar -->
      <div
        class="grey lighten-5 custom-grey-border rounded-lg px-3 py-1 my-2 d-flex align-center justify-space-between flex-wrap ga-2"
      >
        <div class="d-flex align-center">
          <v-checkbox
            v-if="
              !isEnquiry &&
              !isInvoice &&
              !isQuotation &&
              !isPreventive &&
              !isProject
            "
            readonly
            class="my-0 py-0"
            v-model="showPrice"
            color="cyan"
            label="Show Price To Engineer"
            hide-details
          />
        </div>

        <div class="d-flex align-center">
          <label
            for="discount-level"
            class="caption font-weight-700 text-uppercase grey--text text--darken-2 mr-3 mb-0"
          >
            Discount Level:
          </label>
          <v-radio-group
            readonly
            id="discount-level"
            v-model="discountLevel"
            row
            hide-details
            class="ma-0 pa-0"
          >
            <v-radio label="Line Item" value="line_item" color="cyan"></v-radio>
            <v-radio
              label="Transaction"
              value="transaction"
              color="cyan"
            ></v-radio>
          </v-radio-group>
        </div>
      </div>

      <!-- Table Header Bar -->
      <div
        class="grey lighten-4 custom-grey-border rounded-t-lg px-3 py-2 d-flex align-center"
      >
        <div
          class="color-custom-blue caption-1 font-weight-700 text-center"
          style="width: 5%"
        >
          #
        </div>
        <div
          class="color-custom-blue caption-1 font-weight-700 text-left d-flex align-center"
          :style="get_col_style('product')"
        >
          <span>Product / Service / Customer Asset</span>
        </div>
        <div
          class="color-custom-blue caption-1 font-weight-700 text-center"
          style="width: 15%"
        >
          Selling/Unit Cost
        </div>
        <div
          class="color-custom-blue caption-1 font-weight-700 text-center"
          style="width: 10%"
        >
          Quantity
        </div>
        <div
          v-if="discountLevel == 'line_item'"
          class="color-custom-blue caption-1 font-weight-700 text-center"
          style="width: 23%"
        >
          Discount
        </div>
        <div
          class="color-custom-blue caption-1 font-weight-700 text-right"
          style="width: 10%"
        >
          Total
        </div>
      </div>

      <!-- Line Items List -->
      <div class="custom-grey-border rounded-b-lg border-top-0 overflow-hidden">
        <div
          v-for="(row, index) in db_line_items"
          :key="`line-item-drag-${index}`"
          class="px-3 py-1 custom-border-bottom"
          :class="[
            row.type == 'header'
              ? 'orange_lighten-5'
              : index % 2 === 0
              ? 'white'
              : 'orange_lighten-5',
            { 'pl-6': row.is_child },
          ]"
        >
          <div :id="`line-item-drag-${index}`" class="d-flex align-center">
            <!-- Index / Sno -->
            <div
              class="font-weight-700 text-center caption grey--text text--darken-3"
              style="width: 5%"
              v-if="row.product_type != 'header'"
            >
              {{ row.sno }}.
            </div>

            <!-- Section Header Row -->
            <div v-if="row.type == 'header'" class="text-left py-1 width-100">
              <div class="d-flex align-center">
                <v-icon color="cyan darken-2" small class="mr-2"
                  >mdi-folder-outline</v-icon
                >
                <span class="subtitle-2 font-weight-700 color-custom-blue">{{
                  row.product
                }}</span>
              </div>
            </div>

            <!-- Product Item Row -->
            <div
              v-else
              class="text-left d-flex align-center"
              :style="get_col_style('product', row.is_child)"
            >
              <div class="d-flex align-center width-100">
                <v-avatar
                  size="36"
                  class="mr-3 custom-grey-border rounded-pill"
                  v-if="row.product_type != 'other'"
                >
                  <img
                    v-if="row?.image?.url"
                    :src="row?.image?.url"
                    :alt="row?.image?.name"
                  />
                  <img v-else :src="$defaultImage" />
                </v-avatar>

                <div class="flex-grow-1 min-width-0">
                  <div
                    class="d-flex align-items-center justify-space-between flex-wrap ga-1"
                  >
                    <span
                      class="subtitle-1 font-weight-700 color-custom-blue text-capitalize mr-2"
                    >
                      {{ row.product }}
                    </span>

                    <div class="d-flex align-center ga-1">
                      <v-tooltip top content-class="custom-top-tooltip">
                        <template v-slot:activator="{ on, attrs }">
                          <v-chip
                            v-bind="attrs"
                            v-on="on"
                            color="cyan"
                            text-color="white"
                            x-small
                            label
                            class="font-weight-700 text-uppercase"
                          >
                            <template v-if="row.type == 'tools'">T</template>
                            <template v-else-if="row.type == 'parts'"
                              >PR</template
                            >
                            <template v-else-if="row.type == 'equipment'"
                              >CA</template
                            >
                            <template v-else>{{ row.type.charAt(0) }}</template>
                          </v-chip>
                        </template>
                        <span class="text-uppercase">
                          <template v-if="row.type == 'tools'">Tools</template>
                          <template v-else-if="row.type == 'equipment'"
                            >Customer Asset</template
                          >
                          <template v-else>{{ row.type }}</template>
                        </span>
                      </v-tooltip>

                      <v-tooltip
                        top
                        content-class="custom-top-tooltip"
                        v-if="row?.yearly_costs"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-chip
                            v-bind="attrs"
                            v-on="on"
                            color="cyan darken-1"
                            text-color="white"
                            x-small
                            label
                            class="font-weight-700 cursor-pointer ml-1"
                            v-on:click.stop="openYearCostDialog(row)"
                          >
                            <v-icon x-small left>mdi-cog</v-icon>
                            Yearly Cost
                          </v-chip>
                        </template>
                        <span>View Yearly Costs</span>
                      </v-tooltip>

                      <v-chip
                        v-if="row.barcode"
                        label
                        color="blue darken-4"
                        outlined
                        x-small
                        class="font-weight-700 cursor-pointer ml-1"
                        v-on:click="routeToDetail(row)"
                      >
                        {{ row.barcode }}
                      </v-chip>
                    </div>
                  </div>

                  <!-- Description -->
                  <div
                    class="caption grey--text text--darken-1 mt-0 text-truncate"
                  >
                    <template v-if="row.description">
                      {{ row.description }}
                    </template>
                    <template v-else>
                      <em>No description</em>
                    </template>
                  </div>

                  <!-- Item Images -->
                  <div
                    v-if="row && row.item_images && row.item_images.length"
                    class="d-flex flex-wrap mt-2"
                  >
                    <v-img
                      v-for="(nimg, nmlindex) in row.item_images"
                      :key="`new-image-${nmlindex}`"
                      :src="nimg"
                      aspect-ratio="1"
                      height="60"
                      width="60"
                      class="mr-2 mb-1 rounded custom-grey-border"
                    >
                      <template v-slot:placeholder>
                        <v-row
                          class="fill-height ma-0"
                          align="center"
                          justify="center"
                        >
                          <v-progress-circular
                            indeterminate
                            color="grey darken-2"
                            size="20"
                          ></v-progress-circular>
                        </v-row>
                      </template>
                    </v-img>
                  </div>
                </div>
              </div>
            </div>

            <!-- Selling / Unit Cost -->
            <div
              class="text-center font-weight-600 caption-1"
              style="width: 15%"
              v-if="!(row.type == 'header')"
            >
              {{ row.rate }}
            </div>

            <!-- Quantity -->
            <div
              class="text-center font-weight-600 caption-1"
              style="width: 10%"
              v-if="!(row.type == 'header')"
            >
              {{ row.quantity }} <span class="grey--text">{{ row.uom }}</span>
            </div>

            <!-- Discount -->
            <div
              class="text-center"
              style="width: 23%"
              v-if="discountLevel == 'line_item' && !(row.type == 'header')"
            >
              <div class="d-flex align-center">
                <v-text-field
                  readonly
                  hide-details
                  dense
                  outlined
                  solo
                  flat
                  type="number"
                  v-model="row.discount_value"
                  color="cyan"
                  class="caption-1 font-weight-600 mr-1"
                  style="width: 50%"
                />
                <v-select
                  readonly
                  :items="discount_type_list"
                  v-model="row.discount_type"
                  hide-details
                  item-text="type"
                  item-value="value"
                  dense
                  outlined
                  solo
                  flat
                  color="cyan"
                  class="caption-1 font-weight-600"
                  style="width: 50%"
                />
              </div>
            </div>

            <!-- Total -->
            <div
              class="text-right font-weight-700 subtitle-1 color-custom-blue"
              style="width: 10%"
              v-if="!(row.type == 'header')"
            >
              {{ row.total }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Empty State -->
    <template v-else>
      <div class="d-flex flex-column align-center justify-center py-10">
        <v-avatar color="cyan lighten-5" size="64" class="mb-3">
          <v-icon size="36" color="cyan darken-2"
            >mdi-package-variant-closed</v-icon
          >
        </v-avatar>
        <h3 class="subtitle-1 font-weight-700 color-custom-blue mb-1">
          No Line Items Found
        </h3>
        <p class="caption grey--text text--darken-1 mb-0">
          There are no line items added for this project at the moment.
        </p>
      </div>
    </template>

    <!-- Serial Number Dialog -->
    <ViewSerialNumber
      v-if="seleceDialog"
      :transaction-detail="transactionDetail"
      :dialogStatus="seleceDialog"
      v-on:close:dialog="seleceDialog = false"
    >
    </ViewSerialNumber>

    <!-- Yearly Cost Dialog -->
    <Dialog :commonDialog="yearCostDialog" :dialogWidth="600">
      <template v-slot:title>
        <div class="d-flex align-center width-100">
          <v-icon color="cyan darken-2" class="mr-2">mdi-cog</v-icon>
          <span class="font-weight-700 color-custom-blue"
            >{{ barCode ? barCode + " - " : ""
            }}{{ selectedLineItem?.product }}</span
          >
          <v-spacer></v-spacer>
          <v-btn
            icon
            aria-label="Close yearly costs dialog"
            v-on:click="yearCostDialog = false"
          >
            <v-icon color="grey darken-1">mdi-close</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:body>
        <div class="pa-2">
          <v-row
            v-for="(entry, key) in selectedLineItem?.yearly_costs"
            :key="key"
            align="center"
            class="mb-2"
          >
            <v-col cols="12" sm="4">
              <span class="caption-1 font-weight-700 color-custom-blue">
                {{ getOrdinalSuffix(entry.year) }} Year:
              </span>
            </v-col>

            <v-col cols="12" sm="8">
              <v-text-field
                v-model="entry.cost"
                :id="`year-cost-${key}`"
                type="number"
                dense
                color="cyan"
                outlined
                solo
                flat
                hide-details
                placeholder="Cost"
                prefix="$"
                readonly
                class="font-weight-600"
              />
            </v-col>
          </v-row>
        </div>
      </template>

      <template v-slot:action>
        <v-btn
          depressed
          class="custom-grey-border custom-bold-button px-6"
          v-on:click="yearCostDialog = false"
        >
          Close
        </v-btn>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { QUERY } from "@/core/services/store/request.module";
import ViewSerialNumber from "@/view/pages/partials/View-Serial-Number.vue";
import Dialog from "@/view/pages/partials/Dialog";
export default {
  name: "visit-line-item",
  props: {
    showPrice: {
      type: Boolean,
      default: false,
    },
    discountLevel: {
      type: String,
      default: "transaction",
    },
    relatedId: {
      type: Number,
      default: 0,
    },
    isEnquiry: {
      type: Boolean,
      default: false,
    },
    isQuotation: {
      type: Boolean,
      default: false,
    },
    isPreventive: {
      type: Boolean,
      default: false,
    },
    isInvoice: {
      type: Boolean,
      default: false,
    },
    relatedType: {
      type: Number,
      default: 0,
    },

    isProject: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      db_line_items: [],
      discount_type_list: [
        { type: "%", value: "2" },
        { type: "$", value: "1" },
      ],
      sno: 1,
      loading: false,
      transactionDetail: {},
      seleceDialog: false,
      yearCostDialog: false,
      selectedLineItem: null,
    };
  },
  methods: {
    optionSerial(row) {
      this.transactionDetail = row;
      this.seleceDialog = true;
    },

    formatKey(key) {
      const match = key.match(/^([a-zA-Z]+)(\d+)$/);
      if (match) {
        const [, letters, numbers] = match;
        return `${this.capitalize(letters)} ${numbers}`;
      }
      return this.capitalize(key);
    },

    getOrdinalSuffix(n) {
      const lastDigit = n % 10;
      const lastTwoDigits = n % 100;

      if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
        return n + "th";
      }

      switch (lastDigit) {
        case 1:
          return n + "st";
        case 2:
          return n + "nd";
        case 3:
          return n + "rd";
        default:
          return n + "th";
      }
    },

    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    openYearCostDialog(row) {
      this.selectedLineItem = row;
      this.yearCostDialog = true;
    },
    routeToDetailLink(id, type) {
      this.$router.push({
        name: type,
        params: { id },
      });
    },
    routeToDetail(data) {
      let routeName = null;
      if (data.product_type == "equipment") {
        routeName = "client-equipment.detail";
      } else if (data.product_type == "tools") {
        routeName = "equipment.detail";
      } else if (data.product_type == "asset") {
        routeName = "asset-equipment.detail";
      } else if (data.product_type == "goods") {
        routeName = "product.detail";
      } else if (data.product_type == "service") {
        routeName = "service.detail";
      } else if (data.product_type == "parts") {
        routeName = "part.detail";
      }
      if (data && data.product_id && data.product_id > 0) {
        this.$router.replace(
          this.getDefaultRoute(routeName, {
            params: { id: data.product_id },
          })
        );
      }
    },
    get_line_items() {
      this.loading = true;
      this.$store
        .dispatch(QUERY, {
          url: `detail-iteam/${this.relatedId}/line-items`,
          data: {
            type: this.relatedType,
          },
        })
        .then(({ data }) => {
          this.db_line_items = data;
          this.$emit("line-item", this.db_line_items.length);
        })
        .catch((error) => {
          console.log({ error });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    get_col_style(param, child) {
      let width = 45;
      if (param == "product") {
        if (!this.showPrice) {
          width = 85;
        } else if (this.showPrice) {
          if (this.discountLevel == "transaction") {
            width = 60;
          } else {
            width = 45;
          }
        }
      }
      return { width: child ? `${width - 5}%` : `${width}%` };
    },
    /*  get_col_style(param, child) {
      let width = 46;
      if (param == "product") {
        if (!this.show_price) {
          width = 70;
        } else if (this.show_price) {
          width = 46;
          if (this.discount_level == "transaction") {
            width = 70;
          }
        }
      }
      return { width: child ? `${width - 5}%` : `${width}%` };
    }, */
  },
  mounted() {
    this.get_line_items();
  },
  computed: {
    getSN: () => {
      return function (row, index) {
        if (index == 0) {
          this.sno = 1;
          console.log();
        }
        if (row.product_type == "header") {
          this.sno = 1;
        } else {
          this.sno = this.sno + 1;
        }
      };
    },

    barCode() {
      const barcode = this.selectedLineItem?.barcode;
      return barcode ? barcode.replace(/\s*-\s*/g, "") : null;
    },
  },
  components: {
    ViewSerialNumber,
    Dialog,
  },
};
</script>
